/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import { Collapse, Space, DatePicker, TimePicker } from 'antd';
import upperFirst from 'lodash/upperFirst';
import curryN from 'lodash/fp/curryN';
import memoize from 'lodash/fp/memoize';
import moment from 'moment';
import React from 'react';
import clsx from 'clsx';

import { Button } from '@atoms';

import * as s from './styles.module.less';

const scheduleKey = 'schedule';
const dateName = 'date';
const timeName = 'time';

const ContentControls = ({
  saveDraftText = 'SAVE DRAFT',
  handleSaveDraft,
  disableSaveDraft,
  isSavingDraft,

  publishText = 'PUBLISH',
  handlePublish,
  disablePublish,
  isPublishing,
  publishDate: publishDateString,
  enableDelayedPublish = true,

  deleteText = 'DELETE',
  handleDelete,
  disableDelete,
  isDeleting,

  testText = 'TEST',
  handleTest,
  disableTest,
  isTesting,

  previewText = 'View preview',
  handlePreview,
  disablePreview,
  timePicker,
  backDatePublish,

  cancelText = 'CANCEL',
  handleCancel,
  disableCancel,
  isCanceling,
}) => {
  const [dateTime, setDateTime] = React.useState();
  const [visible, setVisible] = React.useState();

  const publishDate = React.useMemo(() => {
    if (publishDateString) {
      return moment(Date.parse(publishDateString));
    }
    return undefined;
  }, [publishDateString]);

  React.useEffect(() => {
    setDateTime(publishDate);
    setVisible(Boolean(publishDate));
  }, [publishDate]);

  const handleToggleDateTime = React.useCallback(
    (opened) => {
      const active = opened.includes(scheduleKey);
      setDateTime(active ? publishDate : undefined);
      setVisible(active);
    },
    [setDateTime, setVisible, publishDate],
  );

  const publishedDateBefore = moment(publishDate).isBefore(moment().startOf('day'));

  const isDateInvalid = (value) => {
    if (publishDate && publishedDateBefore) {
      return moment(value).startOf('day').isAfter(moment().startOf('day'));
    }
    if (backDatePublish) return false;
    return moment(value).startOf('day').isBefore(moment().startOf('day'));
  };

  const isDateTimeInvalid = (value) => {
    if (backDatePublish) return false;
    return value.isBefore(moment());
  };

  const handleChange = React.useCallback(
    memoize(
      curryN(2, (kind, value) => {
        if (kind === dateName && value) {
          value.startOf('day');
        }
        setDateTime(value);
      }),
    ),
    [setDateTime],
  );

  const handlePressPublish = React.useCallback(() => {
    handlePublish(dateTime ? { dateTime: dateTime.toISOString() } : undefined);
  }, [handlePublish, dateTime]);

  return (
    <Space size={20} direction="vertical" style={{ width: '100%' }}>
      {handlePublish && (
        <>
          <Button
            type="primary"
            block
            disabled={
              disablePublish || isSavingDraft || isDeleting || (visible && (!dateTime || isDateTimeInvalid(dateTime)))
            }
            loading={isPublishing}
            onClick={handlePressPublish}
          >
            {publishText}
          </Button>
          {enableDelayedPublish && (
            <Collapse
              bordered={false}
              expandIconPosition="right"
              activeKey={visible ? [scheduleKey] : undefined}
              onChange={handleToggleDateTime}
              className={clsx(s.collapse, visible && s.active)}
            >
              <Collapse.Panel
                key={scheduleKey}
                header={`${upperFirst(publishText.toLowerCase())} ${visible ? 'at' : 'immediately'}`}
              >
                <DatePicker
                  value={dateTime}
                  onChange={handleChange(dateName)}
                  format="DD/MM/YYYY"
                  disabledDate={isDateInvalid}
                />
                <TimePicker
                  value={dateTime}
                  disabled={!dateTime}
                  onChange={handleChange(timeName)}
                  format="HH:mm"
                  disabledDate={isDateTimeInvalid}
                  {...timePicker}
                />
              </Collapse.Panel>
            </Collapse>
          )}
        </>
      )}
      {handlePreview && (
        <Button type="link" block onClick={handlePreview} disabled={disablePreview} className={s.thin}>
          {previewText}
        </Button>
      )}
      {handleTest && (
        <Button
          block
          disabled={disableTest || isPublishing || isSavingDraft || isDeleting}
          loading={isTesting}
          onClick={handleTest}
        >
          {testText}
        </Button>
      )}
      {handleSaveDraft && (
        <Button
          block
          disabled={disableSaveDraft || isPublishing || isSavingDraft || isDeleting}
          loading={isSavingDraft}
          onClick={handleSaveDraft}
        >
          {saveDraftText}
        </Button>
      )}
      {handleDelete && (
        <Button
          block
          disabled={disableDelete || isPublishing || isSavingDraft}
          loading={isDeleting}
          onClick={handleDelete}
        >
          {deleteText}
        </Button>
      )}
      {handleCancel && (
        <Button
          block
          disabled={disableCancel || isPublishing || isSavingDraft || isDeleting}
          loading={isCanceling}
          onClick={handleCancel}
        >
          {cancelText}
        </Button>
      )}
    </Space>
  );
};

export default ContentControls;
